import { PropsWithChildren, createContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

// types
import type { IEntry, ILocation } from '../locator.types';

// utils
import { usePermissionsStatus } from './utils/use-permission-status';
import { geocode, geodecode, getCoordinates } from 'utils/get-coordinates';
import { useSearchParamsState } from 'utils/hooks/use-search-params-state';
import { useIsoCountry } from 'scenes/MetaData';
import { isDefined } from 'utils/ts-utilities';
import { arrayUnique } from 'utils/array-unique';
import { useTracking } from 'utils/tracking/track';

const useValue = ({
  locatorPath,
  entries,
}: {
  locatorPath: string;
  entries?: IEntry<undefined>[];
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const userInput = useRef('');
  const [geoLocated, setGeoLocated] = useState(false);
  const [geoLocationPending, setGeoLocationPending] = useState(false);
  const [error, setError] = useState(false);
  const [searchParamsState, setSearchParamsState] = useSearchParamsState([
    'lat',
    'lng',
    'addr',
    'country',
  ]);
  const isPermitted = usePermissionsStatus();
  const country = useIsoCountry().toLowerCase();
  const { trackLocator } = useTracking();
  const countries = entries
    ? arrayUnique(entries?.map((entry) => entry?.country?.toLowerCase())).filter(isDefined)
    : undefined;

  const location = useMemo((): ILocation | undefined => {
    if (!searchParamsState?.lat || !searchParamsState?.lng) {
      return undefined;
    }

    return {
      coordinates: {
        lat: Number(searchParamsState.lat),
        lng: Number(searchParamsState.lng),
      },
      address: searchParamsState.addr,
    };
  }, [searchParamsState]);

  async function locate() {
    try {
      setError(false);
      setGeoLocationPending(true);

      const coordinates = await getCoordinates();
      if (coordinates) {
        const location = await geodecode({
          lat: Number(coordinates.lat),
          lng: Number(coordinates.lng),
        });
        setGeoLocated(true);
        const params = {
          lat: String(coordinates.lat),
          lng: String(coordinates.lng),
          addr: location?.address ?? '',
        };
        if (pathname === locatorPath) {
          router.push(`${locatorPath ?? ''}?${new URLSearchParams(params).toString()}`);
        } else {
          setSearchParamsState(params);
        }
      }
    } catch (error) {
      setError(true);
    } finally {
      setGeoLocationPending(false);
    }
  }

  async function findLocation(val: string) {
    userInput.current = val;
    const location = await geocode(val, country);
    if (location) {
      setGeoLocated(false);
      const addressComponent = location.address_components.find(
        (address) => address.types[0] === 'country',
      );
      let country: string | undefined = undefined;
      if (searchParamsState.country) {
        if (countries && addressComponent?.short_name) {
          if (countries.includes(addressComponent.short_name.toLowerCase())) {
            country = addressComponent.short_name;
          }
        }
      }

      const params = {
        lat: String(location.coordinates.lat),
        lng: String(location.coordinates.lng),
        addr: location.address ?? '',
        country: country ?? '',
      };

      if (pathname === locatorPath) {
        setSearchParamsState(params);
      } else {
        router.push(`${locatorPath ?? ''}?${new URLSearchParams(params).toString()}`);
      }
    }
  }

  function resetError() {
    setError(false);
  }

  useEffect(() => {
    if (isPermitted) {
      resetError();
    }
  }, [isPermitted]);

  useEffect(() => {
    if (userInput.current) {
      trackLocator({
        click_intent: 'locator search',
        search_term: userInput.current,
        search_term_executed: location?.address,
        value: 2,
        content_type: 'locator-partner',
      });
    }
  }, [location?.address, trackLocator]);

  return {
    location,
    error,
    geoLocated,
    geoLocationPending,
    locate,
    findLocation,
    resetError,
  };
};

export const SearchContext = createContext({} as ReturnType<typeof useValue>);

export const SearchContextProvider = ({
  children,
  locatorPath = '',
  entries,
}: PropsWithChildren<{ locatorPath?: string; entries?: IEntry<undefined>[] }>) => {
  return (
    <SearchContext.Provider value={useValue({ locatorPath, entries })}>
      {children}
    </SearchContext.Provider>
  );
};
